import React, { Suspense, useCallback, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Autocomplete, Box, Button, Grid, IconButton, TablePagination, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { useDispatch, useSelector } from 'react-redux';
import Loader from '../../components/Loader';
import ErrorScreen from '../../components/ErrorScreen';
import SearchField from '../../components/input/SearchField';
import Layout from '../../components/Layout/Layout';
import ModalCustom from '../../components/modals/ModalCustom';
import AddNew from './AddNew';
import Edit from './Edit';
import ModalConfirmation from '../../components/modals/ModalConfirmation';
import { apiRequest } from '../../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { fetchCustomerData } from '../../redux/slices/customerSlice';
import { fetchRepsData } from '../../redux/slices/repSlice';

export default function Customer() {
    let { user } = useSelector((state) => state?.user);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [loadingDelReq, setLoadingDelReq] = useState(false);

    const [customer, setCustomer] = useState(null);

    const [filteredData, setFilteredData] = useState(null);
    const [selectedRep, setSelectedRep] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const dispatch = useDispatch();
    const { data: reps, isLoading: isLoadingReps, error: errorReps } = useSelector((state) => state.rep);
    const { data, isLoading, error } = useSelector((state) => state.customer);

    // ====================================== Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [startIndex, setStartIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(0);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        const sIndex = newPage * rowsPerPage;
        setStartIndex(sIndex);
        setEndIndex(sIndex + rowsPerPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(event.target.value);
        const sIndex = page * event.target.value;
        setStartIndex(sIndex);
        setEndIndex(sIndex + rowsPerPage);
    };

    React.useEffect(() => {
        dispatch(fetchCustomerData());
    }, []);

    React.useEffect(() => {
        if (!reps) {
            dispatch(fetchRepsData());
        }

        // Ensure that the data is available and is an array
        if (Array.isArray(data)) {
            // Calculate the startIndex and endIndex based on the current page and rowsPerPage
            const sIndex = page * rowsPerPage;

            setStartIndex(sIndex);
            setEndIndex(sIndex + rowsPerPage);
            setFilteredData(data);
            // Slice the data to get the paginated subset
            setTotalCount(data?.length);
        }
    }, [data]);

    const handleSearch = useCallback(
        (searchText, rep) => {
            setSearchQuery(searchText);
            // Filter data based on search text and selected Rep
            const filteredResults = data?.filter(
                (row) =>
                    (row?.name?.toLowerCase().includes(searchText?.toLowerCase()) ||
                        row?.email?.toLowerCase().includes(searchText?.toLowerCase()) ||
                        row?.phoneNo?.toLowerCase().includes(searchText?.toLowerCase()) ||
                        row?.phoneNo2?.toLowerCase().includes(searchText?.toLowerCase()) ||
                        row?.address?.toLowerCase().includes(searchText?.toLowerCase())) &&
                    (!rep || row?.user?._id === rep?._id)
            );

            const sIndex = page * rowsPerPage;

            setStartIndex(sIndex);
            setEndIndex(sIndex + rowsPerPage);
            setPage(0);
            setFilteredData(filteredResults);
            setTotalCount(filteredResults?.length);
        },
        [data, page, rowsPerPage]
    );

    const handleDelete = async () => {
        setLoadingDelReq(true);
        try {
            const res = await apiRequest('delete', `customers/${customer?.id}`, {});

            if (res?.data) {
                setCustomer(null);
                dispatch(fetchCustomerData());
                setShowDeleteModal(false);
                return toast.success('Deleted Successfully!');
            }
        } catch (error) {
            toast.error(error?.response?.data?.message ?? error?.response?.data ?? error?.message);
        } finally {
            setLoadingDelReq(false);
        }
    };

    return (
        <Layout>
            <>
                <Grid container sx={{ mb: 2 }} spacing={2} alignItems={'center'}>
                    <Grid item lg={5} sm={3.5} md={6.5} xs={6}>
                        <Typography variant="h6">Customers</Typography>
                    </Grid>

                    {user?.role === 'super-admin' && (
                        <Grid item lg={1.5} sm={3.5} md={2.5} xs={6} sx={{ display: { xs: 'block', sm: 'none' } }}>
                            <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                                Create Customer
                            </Button>
                        </Grid>
                    )}

                    <Grid item lg={2.5} sm={5} md={3} xs={12}>
                        <Autocomplete
                            size="small"
                            options={reps ?? []}
                            getOptionLabel={(rep) => `${rep?.firstName} ${rep?.lastName}`}
                            value={selectedRep}
                            onChange={(event, selected) => {
                                setSelectedRep(selected);
                                handleSearch(searchQuery, selected);
                            }}
                            renderInput={(params) => <TextField {...params} label="Rep" variant="outlined" />}
                        />
                    </Grid>

                    <Grid item lg={3} sm={5} md={3} xs={12}>
                        <SearchField onChange={(e) => handleSearch(e?.target?.value, selectedRep)} label={'Search Customers'} />
                    </Grid>

                    {user?.role === 'super-admin' && (
                        <Grid item lg={1.5} sm={3.5} md={2.5} xs={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                            <Button onClick={() => setShowCreateModal(true)} fullWidth variant="contained">
                                Create Customer
                            </Button>
                        </Grid>
                    )}
                </Grid>
                {isLoading && !data && <Loader />}

                {!!data && (
                    <>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Rep</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Phone No.</TableCell>
                                        <TableCell>Phone No. 2</TableCell>
                                        <TableCell>Address</TableCell>
                                        {user?.role === 'super-admin' && (
                                            <>
                                                <TableCell sx={{ width: '60px' }} />
                                                <TableCell sx={{ width: '60px' }} />
                                            </>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <Suspense fallback={<Typography>Loading...</Typography>}>
                                    <TableBody>
                                        {filteredData?.slice(startIndex, endIndex)?.map((row) => (
                                            <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell component="th" scope="row">
                                                    {row.serialNo}
                                                </TableCell>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>
                                                    {row?.user?.firstName} {row?.user?.lastName}
                                                </TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.phoneNo}</TableCell>
                                                <TableCell>{row.phoneNo2 || '-'}</TableCell>
                                                <TableCell>{row.address}</TableCell>
                                                {user?.role === 'super-admin' && (
                                                    <>
                                                        <TableCell>
                                                            <IconButton
                                                                onClick={() => {
                                                                    setCustomer({ ...row, rep: row?.user });
                                                                    setShowEditModal(true);
                                                                }}
                                                                aria-label="Edit"
                                                                size="small"
                                                            >
                                                                <BorderColorIcon sx={{ color: '#1976d2' }} />
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                onClick={() => {
                                                                    setCustomer(row);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                                aria-label="Delete"
                                                                size="small"
                                                            >
                                                                <DeleteIcon sx={{ color: 'red' }} />
                                                            </IconButton>
                                                        </TableCell>
                                                    </>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Suspense>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 20]}
                        />
                    </>
                )}

                {!!error && <ErrorScreen error={error} />}
            </>

            {showCreateModal && (
                <ModalCustom open={showCreateModal} title={'Create Customer'}>
                    <AddNew onClose={() => setShowCreateModal(false)} />
                </ModalCustom>
            )}

            {showEditModal && (
                <ModalCustom open={showEditModal} title={'Edit Customer'}>
                    <Edit
                        customer={customer}
                        onClose={() => {
                            setShowEditModal(false);
                        }}
                    />
                </ModalCustom>
            )}

            {showDeleteModal && (
                <ModalConfirmation
                    open={showDeleteModal}
                    loading={loadingDelReq}
                    header={'Delete Customer'}
                    description={`Are you sure, you want to delete ${customer?.name}?`}
                    onClickNo={() => setShowDeleteModal(false)}
                    onClickYes={handleDelete}
                />
            )}
        </Layout>
    );
}
